import { useMemo, useState } from "react";
import { buildPosition, daysBetween, isActive, markup, portalLabel } from "../../services/payrollFinanceCalculator";
import { recordRepayment, rejectRepayment } from "../../services/payrollFinanceService";
import { PortalSelect, dateLabel, money, todayIso } from "./shared";

export default function RepaymentsTab({ data, portal, reload, notify }) {
  const [repayPortal, setRepayPortal] = useState(portal === "ALL" ? "ALM" : portal);
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState({}); // withdrawalId -> { selected, principal, interest, interestEdited }

  const open = useMemo(() => buildPosition({
    withdrawals: data.withdrawals, movements: data.movements, repayments: data.repayments, uses: [], asOf: date, portal: repayPortal
  }).rows.filter((row) => row.outstanding > 0), [data, date, repayPortal]);

  const lineOf = (row) => {
    const stored = lines[row.withdrawal.id] || {};
    const principal = stored.principal ?? String(row.outstanding);
    const days = Math.max(0, daysBetween(row.withdrawal.date, date));
    const computed = markup(Number(principal) || 0, days, row.withdrawal.monthlyRate);
    return { selected: Boolean(stored.selected), principal, interest: stored.interestEdited ? stored.interest : String(computed), days, computed };
  };

  const update = (row, patch) => setLines((current) => ({ ...current, [row.withdrawal.id]: { ...(current[row.withdrawal.id] || {}), ...patch } }));
  const selectAll = (selected) => setLines((current) => Object.fromEntries(open.map((row) => [row.withdrawal.id, { ...(current[row.withdrawal.id] || {}), selected }])));

  const chosen = open.map((row) => ({ row, line: lineOf(row) })).filter(({ line }) => line.selected);
  const principalTotal = chosen.reduce((sum, { line }) => sum + (Number(line.principal) || 0), 0);
  const interestTotal = chosen.reduce((sum, { line }) => sum + (Number(line.interest) || 0), 0);

  const submit = async (event) => {
    event.preventDefault();
    try {
      const result = await recordRepayment({
        portal: repayPortal, date, time, reference, note,
        allocations: chosen.map(({ row, line }) => ({ withdrawalId: row.withdrawal.id, principal: Number(line.principal), interest: Number(line.interest) }))
      });
      notify(`Repayment recorded: principal ${money(result.principalTotal)} + interest ${money(result.interestTotal)}.${result.warning ? ` ${result.warning}` : ""}`, Boolean(result.warning));
      setLines({}); setReference(""); setNote("");
      await reload();
    } catch (err) { notify(err.message || "Could not record this repayment.", true); }
  };

  const reject = async (repayment) => {
    const reason = window.prompt(`Reason for rejecting this ${money(repayment.amount)} repayment (the withdrawals become outstanding again):`);
    if (reason === null) return;
    try { await rejectRepayment(repayment, reason); notify("Repayment rejected."); await reload(); }
    catch (err) { notify(err.message || "Could not reject.", true); }
  };

  const history = data.repayments.filter((r) => portal === "ALL" || r.portal === portal)
    .sort((a, b) => `${b.date} ${b.time || ""}`.localeCompare(`${a.date} ${a.time || ""}`));
  const nameOf = (id) => data.withdrawals.find((w) => w.id === id);

  return (
    <>
      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">Record a repayment to the bank</h2></div>
        <form className="panel-body" onSubmit={submit}>
          <div className="form-grid">
            <PortalSelect value={repayPortal} onChange={(value) => { setRepayPortal(value); setLines({}); }} />
            <div className="field"><label>Repayment date</label><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="field"><label>Time</label><input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            <div className="field"><label>Bank reference</label><input className="input" value={reference} onChange={(e) => setReference(e.target.value)} /></div>
            <div className="field field-wide"><label>Note</label><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table className="data-table">
              <thead><tr>
                <th><input type="checkbox" checked={open.length > 0 && chosen.length === open.length} onChange={(e) => selectAll(e.target.checked)} /></th>
                <th>Withdrawn</th><th>Employee</th><th>Outstanding</th><th>Days to {dateLabel(date)}</th><th>Principal repaying</th><th>Interest (1.9%/month)</th><th>Total</th>
              </tr></thead>
              <tbody>
                {open.map((row) => {
                  const line = lineOf(row);
                  return (
                    <tr key={row.withdrawal.id}>
                      <td><input type="checkbox" checked={line.selected} onChange={(e) => update(row, { selected: e.target.checked })} /></td>
                      <td>{dateLabel(row.withdrawal.date, row.withdrawal.time)}</td><td>{row.withdrawal.employeeName}</td>
                      <td>{money(row.outstanding)}</td><td>{line.days}</td>
                      <td><input className="input" style={{ width: 130, padding: 8 }} type="number" min="0" step="0.01" value={line.principal} onChange={(e) => update(row, { principal: e.target.value })} /></td>
                      <td><input className="input" style={{ width: 110, padding: 8 }} type="number" min="0" step="0.1" value={line.interest}
                        onChange={(e) => update(row, { interest: e.target.value, interestEdited: true })} title={`Calculated: ${line.computed}`} /></td>
                      <td>{money((Number(line.principal) || 0) + (Number(line.interest) || 0))}</td>
                    </tr>
                  );
                })}
                {!open.length && <tr><td colSpan={8}>Nothing outstanding on {portalLabel(repayPortal)} as of {dateLabel(date)}.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="pnl-total-row" style={{ marginTop: 12 }}>
            <span>Repaying {chosen.length} withdrawal(s): principal {money(principalTotal)} + interest {money(interestTotal)}</span>
            <strong>{money(principalTotal + interestTotal)}</strong>
          </div>
          <p className="page-copy">The interest is worked out to the repayment date; if the bank's invoice differs by a few rupees, type the bank's figure in the interest box. Interest is added to Operating Expenses as a draft Borrowing Cost entry.</p>
          <button className="btn btn-primary" type="submit" disabled={!chosen.length}>Record Repayment</button>
        </form>
      </section>

      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">Repayment history</h2></div>
        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>Date / time</th><th>Portal</th><th>Reference</th><th>Covers</th><th>Principal</th><th>Interest</th><th>Total paid</th><th /></tr></thead>
            <tbody>
              {history.map((r) => {
                const rejected = !isActive(r);
                return (
                  <tr key={r.id} style={rejected ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
                    <td>{dateLabel(r.date, r.time)}</td><td>{portalLabel(r.portal)}</td><td>{r.reference || "—"}</td>
                    <td style={{ fontSize: 12 }}>{(r.allocations || []).map((a) => { const w = nameOf(a.withdrawalId); return `${w ? `${w.employeeName} ${w.date}` : a.withdrawalId}: ${money(a.principal)}`; }).join(" · ")}{rejected && <div style={{ textDecoration: "none" }}>Rejected: {r.rejectedReason}</div>}</td>
                    <td>{money(r.principalTotal)}</td><td>{money(r.interestTotal)}</td><td><strong>{money(r.amount)}</strong></td>
                    <td>{!rejected && <button className="btn btn-danger" type="button" onClick={() => reject(r)}>Reject</button>}</td>
                  </tr>
                );
              })}
              {!history.length && <tr><td colSpan={8}>No repayments recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
